import { useQuery } from "@apollo/client";
import { useEffect, useMemo } from "react";

import {
  CustomerServiceOrganizationsDocument,
  CustomerServiceOrganizationsQuery,
  CustomerServiceOrganizationsQueryVariables,
} from "../generated/graphql";
import { captureException } from "../lib/errorHandling";

export type CustomerServiceLearningOrganization = {
  id: string;
  name: string | null;
};

export const useCustomerServiceLearningOrganizations = ({ skip = false }: { skip?: boolean } = {}) => {
  const { data, loading, error, refetch } = useQuery<
    CustomerServiceOrganizationsQuery,
    CustomerServiceOrganizationsQueryVariables
  >(CustomerServiceOrganizationsDocument, { skip });

  useEffect(() => {
    if (error) captureException(error, "learning/organizations: load customer service organizations");
  }, [error]);

  const organizations = useMemo<CustomerServiceLearningOrganization[]>(
    () =>
      [...(data?.customerServiceLearningOrganizations ?? [])]
        .map(organization => ({
          id: organization.id,
          name: organization.name ?? null,
        }))
        .sort((left, right) => (left.name ?? left.id).localeCompare(right.name ?? right.id, "de-DE")),
    [data]
  );

  return {
    organizations,
    isLoading: loading,
    error: error ?? null,
    reload: refetch,
  };
};
